import { Component, OnDestroy, OnInit, signal, inject } from '@angular/core';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { SummaryCardAction, SummaryCardComponent, NotificationEmptyComponent } from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { SeriesOptionsType } from 'highcharts';
import { Subject, takeUntil } from 'rxjs';

import { EnvironmentalImpactDelivery, EnvironmentalImpactState } from '../../environmental-impact.models';
import {
  loadEnvironmentalImpactDeliveriesCurrentYear,
  loadEnvironmentalImpactDeliveriesLastYear,
} from '../../store/environmental-impact.actions';

import { selectEnvironmentalImpactsDeliveriesDivided } from '../../store/environmental-impact.selectors';
import { ChartComponent } from '@next/core-lib/chart';
import { DecimalPipe } from '@angular/common';


@Component({
    selector: 'app-eip-deliveries-widget',
    templateUrl: './eip-deliveries-widget.component.html',
    styleUrls: ['./eip-deliveries-widget.component.scss'],
    imports: [
        SummaryCardComponent,
        NotificationEmptyComponent,
        ChartComponent,
        DecimalPipe,
        TranslatePipe,
    ],
})
export class EIPDeliveriesWidgetComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<EnvironmentalImpactState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);

  deliveries = signal<EnvironmentalImpactDelivery[] | undefined>(undefined);
  dataSource = signal<SeriesOptionsType[] | undefined>(undefined);
  chartOptions = signal<Highcharts.Options | undefined>(undefined);
  protected readonly cardActions: SummaryCardAction[] = [
    {
      id: 'info',
      icon: 'info_outline',
      callback: () => this.openInformationDialog(),
    },
  ];
  private readonly destroyed$ = new Subject<boolean>();

  ngOnInit(): void {
    this.store.dispatch(loadEnvironmentalImpactDeliveriesCurrentYear());
    this.store.dispatch(loadEnvironmentalImpactDeliveriesLastYear());
    this.getEipDeliveries();
  }

  initChart(deliveriesData: EnvironmentalImpactDelivery[]): void {
    if (!deliveriesData?.length || deliveriesData.length !== 2) {
      return;
    }
    const prepareScore = (score: number) => (score ? Math.round(score) : 0);

    this.dataSource.set([
      {
        type: 'column',
        name: deliveriesData[0] ? deliveriesData[0].year : '',
        color: '#fecc00',
        data: [
          prepareScore(deliveriesData[0].values.waterLife),
          prepareScore(deliveriesData[0].values.soilLife),
          prepareScore(deliveriesData[0].values.groundwaterWashout),
        ],
      },
      {
        type: 'column',
        name: deliveriesData[1] ? deliveriesData[1].year : '',
        color: '#B2DF8A',
        data: [
          prepareScore(deliveriesData[1].values.waterLife),
          prepareScore(deliveriesData[1].values.soilLife),
          prepareScore(deliveriesData[1].values.groundwaterWashout),
        ],
      },
    ] as SeriesOptionsType[]);

    this.chartOptions.set({
      title: '',
      chart: {
        height: '250px',
      },
      xAxis: {
        categories: [
          this.locale.translate('waterLife'),
          this.locale.translate('soilLife'),
          this.locale.translate('groundwaterWashout'),
        ],
      },
      yAxis: {
        title: {
          text: null,
        },
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        enabled: false,
      },
    } as Highcharts.Options);
  }

  getEipDeliveries(): void {
    this.store.select(selectEnvironmentalImpactsDeliveriesDivided).pipe(takeUntil(this.destroyed$)).subscribe((deliveries) => {
      if (deliveries.length === 2) {
        this.deliveries.set(deliveries);
        this.initChart(deliveries);
      }
    });
  }

  openInformationDialog(): void {
    this.ai.trackEvent('EIPDeliveries.openInformationDialog', {});
    this.dialog.notify(
      {
        title: this.locale.translate('environmentalImpactPointsDeliveriesInfoTitle'),
        text: this.locale.translate('environmentalImpactPointsDeliveriesInfoText'),
      },
      {
        maxWidth: '600px',
      },
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
